import { NetworkStatusDisplayTextMap, NetworkStatuses } from '@strata/constants';
import { addCommas } from '@strata/helper-functions';
import { FormPageContainer } from '@strata/react-form-header-and-content';
import {
    type ColumnDef,
    type FilterCategory,
    GridWithFilters,
    type KpiData,
    PrimaryData,
    SecondaryData,
} from '@strata/react-grid';
import { KpiSmall } from '@strata/react-kpi';
import { useNavigate } from 'react-router';

import { GridHeaderWithInfo } from 'components/shared/GridHeaderWithInfo';
import { NetworkStatusIndicator } from 'components/shared/NetworkStatusIndicator';
import { TimeFrameBanner } from 'components/shared/TimeframeBanner';
import { ViewsControls } from 'components/shared/ViewsControls';
import { AppRoutes } from 'services/routes';
import { useAppSelector } from 'store';
import { type PharmacySummary, useGetPharmaciesQuery } from 'store/api/apertureApi';
import { useGetPayerDetailsQuery } from 'store/api/referenceApi';
import { Grids } from 'store/grids/gridsSlice';
import { useGridState } from 'store/grids/gridsThunks';

const initialSortProperties = [{ id: 'name', desc: false }];

const filterCategories: FilterCategory<PharmacySummary>[] = [
    {
        key: 'networkStatus',
        title: 'Status',
        valueDisplayTextMap: NetworkStatusDisplayTextMap,
    },
];

const kpiData: KpiData<PharmacySummary>[] = [
    {
        label: 'Active Pharmacies',
        value: (data) =>
            data?.filter((pharmacy) => pharmacy.networkStatus === NetworkStatuses.ACTIVE).length,
    },
];

const columns: ColumnDef<PharmacySummary>[] = [
    {
        accessorKey: 'name',
        header: 'Pharmacy',
        size: 200,
        cell: ({ getValue }) => <PrimaryData data={getValue()} />,
    },
    {
        accessorKey: 'referrals',
        header: 'Active Referrals',
        size: 100,
        cell: ({ getValue }) => <SecondaryData data={addCommas(getValue())} />,
    },
    {
        accessorKey: 'winningPct',
        header: () => (
            <GridHeaderWithInfo
                header="Winning %"
                tooltipText="The average percentage a pharmacy wins referrals when participating in FMH Live Referrals bidding"
            />
        ),
        size: 100,
        cell: ({ getValue }) => (
            <KpiSmall value={getValue()} valueType={KpiSmall.ValueTypes.PERCENTAGE} />
        ),
    },
    {
        accessorKey: 'networkStatus',
        header: 'Status',
        size: 100,
        enableSorting: false,
        cell: ({ getValue }) => <NetworkStatusIndicator networkStatus={getValue()} />,
    },
];

export const PharmaciesPage = () => {
    const navigate = useNavigate();
    const { payerId } = useAppSelector((state) => state.auth);

    const { data: payerDetails } = useGetPayerDetailsQuery({ payerId: payerId! });

    const { segmentId } = useAppSelector((state) => state.views);
    const { data, isFetching } = useGetPharmaciesQuery({ segmentId });

    const {
        sort,
        search,
        activeFilters,
        filterPaneWidth,
        updateGridSort,
        updateGridSearch,
        updateGridActiveFilters,
        updateFilterPaneWidth,
    } = useGridState(Grids.PHARMACIES, initialSortProperties);

    return (
        <FormPageContainer>
            <GridWithFilters
                title="Pharmacies"
                secondaryTitle={payerDetails?.payerName}
                headerContent={<TimeFrameBanner />}
                viewsPaneContent={<ViewsControls />}
                data={data}
                isLoading={isFetching}
                sortProperties={sort}
                onSort={updateGridSort}
                enableSearch
                searchText={search}
                onSearch={updateGridSearch}
                filterCategories={filterCategories}
                activeFilters={activeFilters}
                onFilter={updateGridActiveFilters}
                filterPaneRenderWidth={filterPaneWidth}
                onFilterPaneResize={updateFilterPaneWidth}
                kpiData={kpiData}
                columns={columns}
                onRowClick={(rowData) =>
                    navigate(AppRoutes.pharmacyDetails(rowData.npi), {
                        state: {
                            backToDestination: {
                                text: 'Pharmacies',
                                route: AppRoutes.pharmacies,
                            },
                        },
                    })
                }
            />
        </FormPageContainer>
    );
};
